import {useQuery} from "@tanstack/react-query";
import {useTranslation} from "next-i18next";
import React from "react";
import {getSeasonDates} from "../../data/ReactQueries";
import {objectsAttributes} from "../../models/objects";
import GridTable from "./GridTable";
import MinimalCard from "./MinimalCard";
import LastMinuteLabel from "../LastMinuteLabel";

interface PricesProps {
	object: objectsAttributes;
}

function Prices({object}: PricesProps) {
	const {t} = useTranslation();
	const {data: seasonDates, isLoading} = useQuery(
		["seasonDates"],
		getSeasonDates
	);

	function createTableRow(
		fromDate: Date,
		toDate: Date,
		weekPrice: number,
		dayPrice?: number
	) {
		const prices = [
			`${fromDate.toLocaleDateString(undefined, {day: "2-digit", month: "2-digit", year: "numeric"})} - ${toDate.toLocaleDateString(undefined, {day: "2-digit", month: "2-digit", year: "numeric"})}`,
			(dayPrice ? dayPrice : weekPrice / 7).toString() + " €",
			(weekPrice
					? weekPrice
					: dayPrice
						? dayPrice * 7
						: "Auf Anfrage"
			).toString() + " €"
		];
		return prices;
	}

	let preSeasonPrices: string[][] = [];
	let mainSeasonPrices: string[][] = [];
	let afterSeasonPrices: string[][] = [];

	if (!isLoading && seasonDates) {
		seasonDates.sort((a: any, b: any) => a.from_date - b.from_date).map((item) => {
			switch (item.season) {
				case 1:
					// low season
					const lsPriceRow = createTableRow(
						new Date(item.from_date * 1000),
						new Date(item.to_date * 1000),
						object.ls_in_eur_per_week,
						object.ls_in_eur_per_day
					);
					if (new Date(item.to_date * 1000) > new Date()) {
						preSeasonPrices.push(lsPriceRow);
					}
					break;
				case 2:
					// main season
					const msPriceRow = createTableRow(
						new Date(item.from_date * 1000),
						new Date(item.to_date * 1000),
						object.ms_in_eur_per_week
					);
					if (new Date(item.to_date * 1000) > new Date()) {
						mainSeasonPrices.push(msPriceRow);
					}
					break;
				case 3:
					// after season
					const asPriceRow = createTableRow(
						new Date(item.from_date * 1000),
						new Date(item.to_date * 1000),
						object.as_in_eur_per_week,
						object.as_in_eur_per_day
					);
					// only push dates later then now
					if (new Date(item.to_date * 1000) > new Date()) {
						afterSeasonPrices.push(asPriceRow);
					}
					break;
			}
		});
	}

	const days = Object.values(t("common.days", {returnObjects: true})); // 0 = Montag
	const arrivalDay = object.arrival_day ?? 7; // 1 = Montag

	console.log(afterSeasonPrices);

	const afterSeasonRows = []
	if (afterSeasonPrices.length > 0) {
		afterSeasonPrices[0][0] = t("common.allOtherDates");
		afterSeasonRows.push(afterSeasonPrices[0])
	}
	return (
		<div className="space-y-5 2xl:grid grid-cols-7 place-items-center 2xl:space-y-0 max-w-full">
			<div className="space-y-7 lg:col-span-3 max-w-full">
				<LastMinuteLabel object={object}/>
				<h3 className="lg:mb-16">{t("components.prices.seasonalPrices")}</h3>
				{/* filter by date (first date should appear first) */}
				<GridTable
					key={2}
					head={[
						t("common.seasons.main"),
						t("common.pricePerDay"),
						t("common.pricePerWeek")
					]}
					rows={mainSeasonPrices}
					caption={`(${t("common.arrivalDay")}: ${days[arrivalDay - 1]})`}
				/>
				<GridTable
					key={1}
					head={[
						t("common.seasons.pre"),
						t("common.pricePerDay"),
						t("common.pricePerWeek")
					]}
					rows={preSeasonPrices}
				/>
				<GridTable
					key={3}
					head={[
						t("common.seasons.after"),
						t("common.pricePerDay"),
						t("common.pricePerWeek")
					]}
					rows={afterSeasonRows}
				/>

				<div className="shadow-md p-6">
					<p className="italic text-center font-semibold text-leo-orange text-[16px]">
						{t("components.prices.longTermRentText")}
					</p>
				</div>
			</div>

			<div className="lg:col-span-1 lg:h-full">
				<div
					className="
        bg-leo-divider-gray-2 w-full h-[2px] my-16 
        lg:h-full lg:w-[3px] lg:my-0
        "
				/>
			</div>

			<div className="space-y-7 lg:col-span-3 max-w-full lg:place-self-start">
				<h3 className="lg:mb-16 2xl:mt-0 mt-16">
					{t("components.prices.additionalCosts")}
				</h3>
				<MinimalCard
					cost={parseInt(object.end_cleaning)}
					title={t("components.prices.cleaning.title")}
					description={t("components.prices.cleaning.description")}
				/>
				<MinimalCard
					cost={parseInt(object.deposit)}
					title={t("components.prices.caution.title")}
					description={t("components.prices.caution.description")}
				/>
				<MinimalCard
					title={t("components.prices.visitorsTax.title")}
					description={t("components.prices.visitorsTax.description")}
					extraInformation={t("components.prices.visitorsTax.extraInfo")}
				/>
			</div>
		</div>
	);
}

export default Prices;
